import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NoDataAvailable from '@/components/cards/NoDataAvailable';
import { EnvelopeDetailsSkeleton } from "@/components/skeletons";
import { NO_DOCUMENTS_FOUND } from '@/utils/constants/messages';
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import StatusBadge from "@/components/badges/StatusBadge";
import EnvelopRecipientCard from "@/components/cards/EnvelopeRecipientCard";
import ListCard from "@/components/cards/ListCard";
import { DocumentListItemSkeleton } from "@/components/skeletons";
import DocumentModal from "@/components/modals/DocumentModal";
import { getEnvelopeById, updateEnvelopeReviewStatus } from "@/services/envelope.service";
import { concatenateName, DateFormatter, TimeFormatter } from "@/utils/functions/helperFunctions";
import AppButton from "@/components/buttons/AppButton";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";
import { BUTTONS, REPORTED_USER_TABLE, REVIEW_STATUS, SUCCESS_MESSAGES } from "@/utils/constants/appConstants/commonConstants";
import RejectEnvelopeModal from "@/components/modals/RejectEnvelopeModal";
import { successToasterMessage, errorToasterMessage } from "@/utils/functions/toaster";
import { contactUser, markAsSafe, suspendUser } from "@/services/user.service";
import ContactModal from "@/components/modals/ContactModal";
import SuspendUserModal from "@/components/modals/SuspendUserModal";

type Props = {};

const ReportedEnvelopeDetails = (props: Props) => {
    const [envelopeDetails, setEnvelopeDetails] = useState<any>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [openDocModal, setOpenDocModal] = useState<boolean>(false);
    const [documentContent, setDocumentContent] = useState<any>({});
    const [createdBy, setCreatedBy] = useState<any>({});
    const [rejectReason, setRejectReason] = useState<string>('');
    const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [showSuspendModal, setShowSuspendModal] = useState<boolean>(false);
    const [showContactModal, setShowContactModal] = useState<boolean>(false);
    const [statusLoading, setStatusLoading] = useState<boolean>(false);
    const [contactLoading, setContactLoading] = useState<boolean>(false);
    const isAdminPortalReviewReportedEnvelopePermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalReviewReportedUserEnvelopes);
    const isAdminPortalSuspentionPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalSuspendUser);
    const isAdminPortalUpdateReportedUserPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalUpdateReportedUser);
    const isViewDocumentPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalViewDocument);
    const navigate = useNavigate();
    const params = useParams();
    const id = params.id || "";

    useEffect(() => {
        setLoading(true);
        getEnvelopeById(id, (response: any) => {
            setLoading(false);
            setEnvelopeDetails(response.data);
            setCreatedBy(response.data.created_by);
        }, (error: any) => {
            setLoading(false);
            console.log(error);
        }, { "get_created_by": true });
    }, []);

    const handleOpenDocument = (document: { title: string; sasurl?: string }) => {
        if (!isViewDocumentPermitted || !document.sasurl) return;
        setDocumentContent({ title: document.title, content: document.sasurl });
        setOpenDocModal(true);
    };

    const handleReview = (status: string) => {
        if (id) {
            updateEnvelopeReviewStatus(id, { review_status: status, ...(rejectReason && { optional_message: rejectReason }) })
                .then(() => navigate('/'))
                .catch(console.log);
        }
    };

    const handleReject = () => {
        if (rejectReason.trim()) { handleReview(REVIEW_STATUS.REJECTED); setShowRejectModal(false); }
    };

    const handleSuspendClick = (userId: string) => { setSelectedUserId(userId); setShowSuspendModal(true); };

    const handleMarkAsSafe = (userId: string) => {
        setStatusLoading(true);
        markAsSafe(userId, (response: any) => { successToasterMessage(SUCCESS_MESSAGES.USER_MARKED_SAFE); setStatusLoading(false); }, (err: any) => { errorToasterMessage(err.response.data.message); setStatusLoading(false); });
    };

    const suspendUserhandler = (reason: string) => {
        if (!selectedUserId) return;
        setStatusLoading(true);
        suspendUser({ user_id: selectedUserId, reason: reason }, (response: any) => { successToasterMessage(SUCCESS_MESSAGES.USER_SUSPENDED); setStatusLoading(false); setShowSuspendModal(false); }, (err: any) => { errorToasterMessage(err.response.data.message); setStatusLoading(false); setShowSuspendModal(false); });
    };

    const handleContactUser = (userId: string) => { setSelectedUserId(userId); setShowContactModal(true); };

    const handleContactSubmit = async (subject: string, body: string) => {
        if (!selectedUserId) return;
        setContactLoading(true);
        try {
            await contactUser(selectedUserId, { subject, message: body }, (response: any) => { successToasterMessage(REPORTED_USER_TABLE.MESSAGE_SENT_SUCCESS); setShowContactModal(false); setContactLoading(false); }, (err: any) => { errorToasterMessage(err.response.data.message); setContactLoading(false); });
        } catch (error) { setContactLoading(false); }
    };

    return loading ? (
        <EnvelopeDetailsSkeleton />
    ) : (
        <>
            <div className="flex items-start justify-between gap-3 mb-4">
                <h3 className="text-xl font-normal leading-snug text-secondary" id='envelope-detail-subject'>
                    {envelopeDetails ? envelopeDetails?.meta_data?.email_subject : "Loading"}
                </h3>
                <div className="flex flex-wrap gap-2">
                    {envelopeDetails.review_status === REVIEW_STATUS.PENDING ? (
                        <>
                            {isAdminPortalReviewReportedEnvelopePermitted && <AppButton id='reported-envelope-detail-approve-button' btnText={BUTTONS.APPROVED} onClick={() => handleReview(REVIEW_STATUS.APPROVED)} className={"btn-primary"} />}
                            <AppButton id='reported-envelope-detail-reject-button' btnText={BUTTONS.REJECTED} onClick={() => setShowRejectModal(true)} className={"btn-danger"} />
                            {isAdminPortalReviewReportedEnvelopePermitted && <AppButton id='reported-envelope-detail-approve-with-flag-button' btnText={BUTTONS.APPROVED_WITH_FLAG} onClick={() => handleReview(REVIEW_STATUS.FLAGGED)} className={"btn-yellow"} />}
                        </>
                    ) : (
                        <>
                            {isAdminPortalSuspentionPermitted && <AppButton id='reported-envelope-detail-suspend-user-button' btnText={REPORTED_USER_TABLE.SUSPEND_USER} onClick={() => handleSuspendClick(envelopeDetails.created_by.id)} className={"btn-primary"} />}
                            {isAdminPortalUpdateReportedUserPermitted && <AppButton id='reported-envelope-detail-mark-as-safe-button' btnText={REPORTED_USER_TABLE.MARK_AS_SAFE} onClick={() => handleMarkAsSafe(envelopeDetails.created_by.id)} className={"btn-primary"} />}
                            <AppButton id='reported-envelope-detail-contact-user-button' btnText={REPORTED_USER_TABLE.CONTACT_USER} onClick={() => handleContactUser(envelopeDetails.created_by.id)} className={"btn-primary"} />
                        </>
                    )}
                </div>
            </div>
            <div className="gs-list-page-card mb-4">
                <ul className="list-none m-0 p-0">
                    <ListCard id='reported-envelope-detail-id' label="Envelope ID" value={envelopeDetails.id} />
                    <ListCard id='reported-envelope-detail-status' label="Status" value={<StatusBadge id='reported-envelope-detail' envelope={envelopeDetails} title="details" />} />
                    <ListCard id='reported-envelope-detail-from' label="From" value={concatenateName(envelopeDetails?.created_by?.first_name, envelopeDetails?.created_by?.last_name)} />
                    <ListCard id='reported-envelope-detail-created-at' label="Created at" value={`${DateFormatter(envelopeDetails.created_at)} | ${TimeFormatter(envelopeDetails.created_at)}`} />
                    {envelopeDetails.sent_at && envelopeDetails.status !== "draft" && <ListCard id='reported-envelope-detail-sent-at' label="Sent at" value={`${DateFormatter(envelopeDetails.sent_at)} | ${TimeFormatter(envelopeDetails.sent_at)}`} />}
                    <ListCard id='reported-envelope-detail-changed-at' label="Last changed at" value={`${DateFormatter(envelopeDetails.updated_at)} | ${TimeFormatter(envelopeDetails.updated_at)}`} />
                    <ListCard id='reported-envelope-detail-expired-at' label="Expired at" value={`${DateFormatter(envelopeDetails.expiry_at)} | ${TimeFormatter(envelopeDetails.expiry_at)}`} valueClass="text-danger" />
                    <ListCard id='reported-envelope-detail-reason' label="Reason" value={createdBy?.history?.filter((history: any) => history?.data?.envelope === envelopeDetails.id)?.map((history: any) => history?.reason).join(", ")} />
                </ul>
            </div>
            <div className="gs-list-page-card mb-4">
                <EnvelopRecipientCard recipients={envelopeDetails?.recipients ? envelopeDetails?.recipients.sort((a: { priority: number }, b: { priority: number }) => a.priority - b.priority) : []} />
            </div>
            <div className="gs-list-page-card mb-4">
                <ul className="list-none m-0 p-0">
                    {!loading ? envelopeDetails.documents?.length > 0 ? (
                        envelopeDetails?.documents?.map((document: any, key: any) => (
                            <li className="flex items-center gap-4 px-5 py-3 border-b border-muted last:border-b-0" key={key}>
                                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white shrink-0">
                                    <em className="icon ni ni-file-pdf" id={`envelope-detail-document-icon-${key}`} />
                                </div>
                                {isViewDocumentPermitted ? (
                                    <button
                                        type="button"
                                        onClick={() => handleOpenDocument(document)}
                                        className="flex-1 min-w-0 border-0 bg-transparent p-0 text-sm text-left text-primary hover:underline"
                                        id={`envelope-detail-document-title-${key}`}
                                    >
                                        {document.title}
                                    </button>
                                ) : (
                                    <div
                                        className="flex-1 min-w-0 text-sm text-left text-primary"
                                        id={`envelope-detail-document-title-${key}`}
                                    >
                                        {document.title}
                                    </div>
                                )}
                            </li>
                        ))
                    ) : (
                        <li className="px-5 py-3"><NoDataAvailable><p id='envelope-detail-no-doc-found-message'>{NO_DOCUMENTS_FOUND}</p></NoDataAvailable></li>
                    ) : (
                        <DocumentListItemSkeleton />
                    )}
                </ul>
            </div>
            {isViewDocumentPermitted ? (
                <DocumentModal content={documentContent} show={openDocModal} onHide={() => setOpenDocModal(false)} />
            ) : null}
            <RejectEnvelopeModal show={showRejectModal} onHide={() => setShowRejectModal(false)} onReject={handleReject} reason={rejectReason} setReason={setRejectReason} />
            <SuspendUserModal show={showSuspendModal} onHide={() => setShowSuspendModal(false)} onSuspend={suspendUserhandler} loading={statusLoading} />
            <ContactModal show={showContactModal} onHide={() => setShowContactModal(false)} onContact={handleContactSubmit} loading={contactLoading} />
        </>
    );
};

export default ReportedEnvelopeDetails;
